import { useState } from "react";
import { PenLine, Loader2, Copy, Wand2 } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import AiText from "@/components/AiText";
import api from "@/lib/api";
import { toast } from "sonner";

const KINDS = ["Blog intro", "Full blog post", "LinkedIn post", "Landing page section", "Email newsletter", "Tweet/X thread"];

export default function ContentStudio() {
  const [kind, setKind] = useState("Blog intro");
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [denied, setDenied] = useState(false);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic first."); return; }
    setLoading(true);
    setContent("");
    try {
      const { data } = await api.post("/ai/content", { topic, kind });
      setContent(data.content);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) setDenied(true);
      toast.error(err.response?.data?.detail || "Could not generate content.");
    } finally {
      setLoading(false);
    }
  };

  if (denied) {
    return (
      <div>
        <PageHeader title="Content Studio" subtitle="AI marketing copy generator." showDemo={false} />
        <Panel className="p-12 text-center"><p className="text-white font-medium">Admin access required</p></Panel>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Content Studio" subtitle="Generate on-brand marketing copy with AI." showDemo={false} />
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <Panel className="p-6 space-y-4">
          <div>
            <Label className="text-slate-300 text-xs">Content type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="content-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Topic</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Why small businesses should use AI support agents" className="mt-1.5 bg-white/[0.03] border-white/12 text-white" data-testid="content-topic" />
          </div>
          <Button onClick={generate} disabled={loading} className="w-full h-11 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold gap-2" data-testid="content-generate">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {loading ? "Writing…" : "Generate content"}
          </Button>
        </Panel>

        <Panel className="p-6 min-h-[300px]" data-testid="content-result">
          {content ? (
            <>
              <div className="flex justify-end mb-3">
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(content); toast.success("Copied"); }} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full gap-1.5" data-testid="content-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
              </div>
              <AiText text={content} />
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <PenLine className="h-8 w-8 text-[#4F8CFF]/50" />
              <p className="mt-3 text-sm text-slate-500">Your generated copy will appear here.</p>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
